import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import {
  Users, Globe, Mail, BarChart3, Plus, Trash2, Shield,
  ArrowLeft, Ban, UserCheck, Activity, TrendingUp, Clock, RefreshCw, FileText, Edit2, MessageCircle, Eye, Reply, Send, Loader2
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [recentEmails, setRecentEmails] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [newPost, setNewPost] = useState({ title: '', slug: '', content: '', is_published: true });
  const [contactMessages, setContactMessages] = useState([]);
  const [viewingMessage, setViewingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const fetchAllData = useCallback(async () => {
    try {
      const [statsRes, usersRes, domainsRes, emailsRes, blogRes, contactRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeader()),
        axios.get(`${API}/admin/users`, getAuthHeader()),
        axios.get(`${API}/admin/domains`, getAuthHeader()),
        axios.get(`${API}/admin/emails/recent?limit=20`, getAuthHeader()),
        axios.get(`${API}/admin/blog`, getAuthHeader()),
        axios.get(`${API}/admin/contact`, getAuthHeader())
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setDomains(domainsRes.data);
      setRecentEmails(emailsRes.data);
      setBlogPosts(blogRes.data);
      setContactMessages(contactRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const createDomain = async () => {
    if (!newDomain) {
      toast.error('Please enter a domain name');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/domains`, {
        name: newDomain
      }, getAuthHeader());

      setDomains([response.data, ...domains]);
      setNewDomain('');
      setDomainDialogOpen(false);
      toast.success('Domain created!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create domain';
      toast.error(message);
    }
  };

  const toggleDomain = async (domainId) => {
    try {
      const response = await axios.patch(`${API}/admin/domains/${domainId}`, {}, getAuthHeader());
      setDomains(domains.map(d => d.id === domainId ? { ...d, is_active: response.data.is_active } : d));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to toggle domain');
    }
  };

  const deleteDomain = async (domainId) => {
    try {
      await axios.delete(`${API}/admin/domains/${domainId}`, getAuthHeader());
      setDomains(domains.filter(d => d.id !== domainId));
      toast.success('Domain deleted');
    } catch (error) {
      toast.error('Failed to delete domain');
    }
  };

  const toggleUserBan = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { is_banned: !currentStatus }, getAuthHeader());
      setUsers(users.map(u => u.id === userId ? { ...u, is_banned: !currentStatus } : u));
      toast.success(currentStatus ? 'User unbanned' : 'User banned');
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const toggleUserAdmin = async (userId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { is_admin: !currentStatus }, getAuthHeader());
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: !currentStatus } : u));
      toast.success(currentStatus ? 'Admin removed' : 'Admin granted');
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This will delete all user data.')) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeader());
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  // Blog functions
  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const createBlogPost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error('Please fill in title and content');
      return;
    }

    const slug = newPost.slug || generateSlug(newPost.title);

    try {
      const response = await axios.post(`${API}/admin/blog`, {
        title: newPost.title,
        slug: slug,
        content: newPost.content,
        is_published: newPost.is_published
      }, getAuthHeader());

      setBlogPosts([response.data, ...blogPosts]);
      setNewPost({ title: '', slug: '', content: '', is_published: true });
      setBlogDialogOpen(false);
      toast.success('Blog post created!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create post';
      toast.error(message);
    }
  };

  const updateBlogPost = async () => {
    if (!editingPost) return;

    try {
      const response = await axios.patch(`${API}/admin/blog/${editingPost.id}`, {
        title: editingPost.title,
        slug: editingPost.slug,
        content: editingPost.content,
        is_published: editingPost.is_published
      }, getAuthHeader());

      setBlogPosts(blogPosts.map(p => p.id === editingPost.id ? response.data : p));
      setEditingPost(null);
      toast.success('Post updated!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update post';
      toast.error(message);
    }
  };

  const deleteBlogPost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/admin/blog/${postId}`, getAuthHeader());
      setBlogPosts(blogPosts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const togglePostPublished = async (post) => {
    try {
      const response = await axios.patch(`${API}/admin/blog/${post.id}`, {
        is_published: !post.is_published
      }, getAuthHeader());
      setBlogPosts(blogPosts.map(p => p.id === post.id ? response.data : p));
      toast.success(response.data.is_published ? 'Post published' : 'Post unpublished');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  // Contact message functions
  const markContactMessageRead = async (messageId, currentStatus) => {
    try {
      const response = await axios.patch(`${API}/admin/contact/${messageId}`, {}, getAuthHeader());
      setContactMessages(contactMessages.map(m =>
        m.id === messageId ? { ...m, is_read: response.data.is_read } : m
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const deleteContactMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await axios.delete(`${API}/admin/contact/${messageId}`, getAuthHeader());
      setContactMessages(contactMessages.filter(m => m.id !== messageId));
      if (viewingMessage?.id === messageId) setViewingMessage(null);
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      support: 'bg-blue-500/20 text-blue-400',
      legal: 'bg-purple-500/20 text-purple-400',
      abuse: 'bg-red-500/20 text-red-400',
      business: 'bg-green-500/20 text-green-400'
    };
    return colors[category] || 'bg-gray-500/20 text-gray-400';
  };

  const sendReply = async () => {
    if (!replyingTo || !replyContent.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/contact/reply`, {
        to_email: replyingTo.email,
        to_name: replyingTo.name,
        subject: `Re: ${replyingTo.subject}`,
        message: replyContent,
        original_message_id: replyingTo.id
      }, getAuthHeader());

      toast.success(`Reply sent to ${replyingTo.email}`);
      setContactMessages(contactMessages.map(m =>
        m.id === replyingTo.id ? { ...m, is_read: true } : m
      ));
      setReplyingTo(null);
      setReplyContent('');
      setViewingMessage(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send reply';
      toast.error(message);
    } finally {
      setSendingReply(false);
    }
  };

  // Mock chart data based on stats
  const chartData = stats ? [
    { name: 'Mon', emails: Math.floor(stats.total_emails_received * 0.1), users: Math.floor(stats.total_users * 0.7) },
    { name: 'Tue', emails: Math.floor(stats.total_emails_received * 0.15), users: Math.floor(stats.total_users * 0.75) },
    { name: 'Wed', emails: Math.floor(stats.total_emails_received * 0.2), users: Math.floor(stats.total_users * 0.8) },
    { name: 'Thu', emails: Math.floor(stats.total_emails_received * 0.18), users: Math.floor(stats.total_users * 0.85) },
    { name: 'Fri', emails: Math.floor(stats.total_emails_received * 0.22), users: Math.floor(stats.total_users * 0.9) },
    { name: 'Sat', emails: Math.floor(stats.total_emails_received * 0.08), users: Math.floor(stats.total_users * 0.95) },
    { name: 'Sun', emails: Math.floor(stats.total_emails_received * 0.07), users: stats.total_users },
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-heading text-xl font-semibold">Admin Panel</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAllData}
            data-testid="refresh-admin-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-400' },
            { label: 'Emails Received', value: stats?.total_emails_received || 0, icon: Mail, color: 'text-green-400' },
            { label: 'Temp Addresses', value: stats?.total_temp_addresses || 0, icon: Activity, color: 'text-purple-400' },
            { label: 'Active Domains', value: stats?.active_domains || 0, icon: Globe, color: 'text-orange-400' },
          ].map((stat, index) => (
            <Card key={index} className="bg-card border-border" data-testid={`stat-card-${index}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-3xl font-heading font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg bg-secondary flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Today Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Emails Today</p>
                  <p className="text-2xl font-heading font-bold">{stats?.emails_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Opened Emails Today</p>
                  <p className="text-2xl font-heading font-bold">{stats?.opened_emails_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">New Users Today</p>
                  <p className="text-2xl font-heading font-bold">{stats?.new_users_today || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Email Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(155 30% 15%)" />
                    <XAxis dataKey="name" stroke="hsl(155 20% 60%)" fontSize={12} />
                    <YAxis stroke="hsl(155 20% 60%)" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(155 30% 7%)',
                        border: '1px solid hsl(155 30% 15%)',
                        borderRadius: '8px'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="emails"
                      stroke="hsl(145 80% 45%)"
                      fill="hsl(145 80% 45% / 0.2)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                User Growth
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(155 30% 15%)" />
                    <XAxis dataKey="name" stroke="hsl(155 20% 60%)" fontSize={12} />
                    <YAxis stroke="hsl(155 20% 60%)" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(155 30% 7%)',
                        border: '1px solid hsl(155 30% 15%)',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="users" fill="hsl(170 60% 40%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-secondary">
            <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="domains" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="domains-tab">
              <Globe className="w-4 h-4 mr-2" />
              Domains
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="emails-tab">
              <Mail className="w-4 h-4 mr-2" />
              Recent Emails
            </TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="blog-tab">
              <FileText className="w-4 h-4 mr-2" />
              Blog
            </TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="contact-tab">
              <MessageCircle className="w-4 h-4 mr-2" />
              Contact
              {contactMessages.filter(m => !m.is_read).length > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">
                  {contactMessages.filter(m => !m.is_read).length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">User Management</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Username</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(user => (
                        <TableRow key={user.id} className="border-border" data-testid={`user-row-${user.id}`}>
                          <TableCell className="font-medium">{user.username}</TableCell>
                          <TableCell className="text-muted-foreground">{user.email}</TableCell>
                          <TableCell>
                            <Badge variant={user.is_banned ? "destructive" : "default"} className={user.is_banned ? "" : "bg-green-500/20 text-green-400"}>
                              {user.is_banned ? 'Banned' : 'Active'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={user.is_admin ? "border-primary text-primary" : ""}>
                              {user.is_admin ? 'Admin' : 'User'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant={user.is_banned ? "outline" : "destructive"}
                                onClick={() => toggleUserBan(user.id, user.is_banned)}
                                data-testid={`ban-user-${user.id}`}
                              >
                                <Ban className="w-3 h-3 mr-1" />
                                {user.is_banned ? 'Unban' : 'Ban'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => toggleUserAdmin(user.id, user.is_admin)}
                                data-testid={`toggle-admin-${user.id}`}
                              >
                                <Shield className="w-3 h-3 mr-1" />
                                {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => deleteUser(user.id)}
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Domains Tab */}
          <TabsContent value="domains">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Domain Management</CardTitle>
                <Dialog open={domainDialogOpen} onOpenChange={setDomainDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-primary text-primary-foreground" data-testid="add-domain-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Domain
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Add New Domain</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Domain Name</Label>
                        <Input
                          placeholder="example.com"
                          value={newDomain}
                          onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                          className="bg-input/50 border-transparent focus:border-primary"
                          data-testid="new-domain-input"
                        />
                      </div>
                      <Button
                        onClick={createDomain}
                        className="w-full rounded-full bg-primary text-primary-foreground"
                        data-testid="confirm-add-domain-btn"
                      >
                        Add Domain
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Domain</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {domains.map(domain => (
                        <TableRow key={domain.id} className="border-border" data-testid={`domain-row-${domain.id}`}>
                          <TableCell className="font-mono font-medium">{domain.name}</TableCell>
                          <TableCell>
                            <Badge variant={domain.is_active ? "default" : "secondary"} className={domain.is_active ? "bg-green-500/20 text-green-400" : ""}>
                              {domain.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(domain.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={domain.is_active}
                                  onCheckedChange={() => toggleDomain(domain.id)}
                                  data-testid={`toggle-domain-${domain.id}`}
                                />
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => deleteDomain(domain.id)}
                                data-testid={`delete-domain-${domain.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recent Emails Tab */}
          <TabsContent value="emails">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">Recent Emails</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>From</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Received</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentEmails.map((email, index) => (
                        <TableRow key={email.id || index} className="border-border" data-testid={`email-row-${index}`}>
                          <TableCell className="font-medium truncate max-w-[150px]">{email.from_address}</TableCell>
                          <TableCell className="font-mono text-sm truncate max-w-[150px]">{email.to_address}</TableCell>
                          <TableCell className="truncate max-w-[200px]">{email.subject}</TableCell>
                          <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                            {formatDate(email.received_at)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={email.is_read ? "secondary" : "default"} className={!email.is_read ? "bg-primary" : ""}>
                              {email.is_read ? 'Read' : 'Unread'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                      {recentEmails.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            No emails received yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Blog Management</CardTitle>
                <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-primary text-primary-foreground" data-testid="add-blog-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Post
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-heading">Create Blog Post</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          placeholder="Post title"
                          value={newPost.title}
                          onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                          className="bg-input/50 border-transparent focus:border-primary"
                          data-testid="new-post-title"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Slug (optional - auto-generated from title)</Label>
                        <Input
                          placeholder="post-url-slug"
                          value={newPost.slug}
                          onChange={(e) => setNewPost({ ...newPost, slug: e.target.value })}
                          className="bg-input/50 border-transparent focus:border-primary"
                          data-testid="new-post-slug"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Content (HTML supported)</Label>
                        <Textarea
                          placeholder="Write your blog post content here..."
                          value={newPost.content}
                          onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                          className="bg-input/50 border-transparent focus:border-primary min-h-[200px]"
                          data-testid="new-post-content"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={newPost.is_published}
                          onCheckedChange={(checked) => setNewPost({ ...newPost, is_published: checked })}
                          data-testid="new-post-published"
                        />
                        <Label>Publish immediately</Label>
                      </div>
                      <Button
                        onClick={createBlogPost}
                        className="w-full rounded-full bg-primary text-primary-foreground"
                        data-testid="confirm-create-post-btn"
                      >
                        Create Post
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Title</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blogPosts.map(post => (
                        <TableRow key={post.id} className="border-border" data-testid={`blog-row-${post.id}`}>
                          <TableCell className="font-medium max-w-[200px] truncate">{post.title}</TableCell>
                          <TableCell className="font-mono text-sm text-muted-foreground">{post.slug}</TableCell>
                          <TableCell>
                            <Badge variant={post.is_published ? "default" : "secondary"} className={post.is_published ? "bg-green-500/20 text-green-400" : ""}>
                              {post.is_published ? 'Published' : 'Draft'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(post.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Switch
                                checked={post.is_published}
                                onCheckedChange={() => togglePostPublished(post)}
                                data-testid={`toggle-publish-${post.id}`}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingPost({ ...post })}
                                data-testid={`edit-post-${post.id}`}
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => deleteBlogPost(post.id)}
                                data-testid={`delete-post-${post.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {blogPosts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            No blog posts yet. Create your first post!
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Messages Tab */}
          <TabsContent value="contact">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading">Contact Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>From</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Received</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contactMessages.map(message => (
                        <TableRow key={message.id} className="border-border" data-testid={`contact-row-${message.id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{message.name}</p>
                              <p className="text-sm text-muted-foreground">{message.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getCategoryColor(message.category)}>
                              {message.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{message.subject}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(message.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant={message.is_read ? "secondary" : "default"} className={!message.is_read ? "bg-primary" : ""}>
                              {message.is_read ? 'Read' : 'Unread'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setViewingMessage(message)}
                                data-testid={`view-contact-${message.id}`}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => markContactMessageRead(message.id, message.is_read)}
                                data-testid={`toggle-read-${message.id}`}
                              >
                                {message.is_read ? 'Mark Unread' : 'Mark Read'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => deleteContactMessage(message.id)}
                                data-testid={`delete-contact-${message.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {contactMessages.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No contact messages yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Post Dialog */}
        <Dialog open={!!editingPost} onOpenChange={(open) => !open && setEditingPost(null)}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Edit Blog Post</DialogTitle>
            </DialogHeader>
            {editingPost && (
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingPost.title}
                    onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                    className="bg-input/50 border-transparent focus:border-primary"
                    data-testid="edit-post-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input
                    value={editingPost.slug}
                    onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })}
                    className="bg-input/50 border-transparent focus:border-primary"
                    data-testid="edit-post-slug"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content (HTML supported)</Label>
                  <Textarea
                    value={editingPost.content}
                    onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                    className="bg-input/50 border-transparent focus:border-primary min-h-[200px]"
                    data-testid="edit-post-content"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingPost.is_published}
                    onCheckedChange={(checked) => setEditingPost({ ...editingPost, is_published: checked })}
                    data-testid="edit-post-published"
                  />
                  <Label>Published</Label>
                </div>
                <Button
                  onClick={updateBlogPost}
                  className="w-full rounded-full bg-primary text-primary-foreground"
                  data-testid="confirm-update-post-btn"
                >
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Contact Message Dialog */}
        <Dialog open={!!viewingMessage} onOpenChange={(open) => !open && setViewingMessage(null)}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Contact Message</DialogTitle>
            </DialogHeader>
            {viewingMessage && (
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">From</Label>
                    <p className="font-medium">{viewingMessage.name}</p>
                    <p className="text-sm text-primary">{viewingMessage.email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Category</Label>
                    <p>
                      <Badge className={getCategoryColor(viewingMessage.category)}>
                        {viewingMessage.category}
                      </Badge>
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Subject</Label>
                  <p className="font-medium">{viewingMessage.subject}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Message</Label>
                  <div className="bg-secondary/30 border border-border rounded-lg p-4 mt-1 whitespace-pre-wrap">
                    {viewingMessage.message}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Received: {new Date(viewingMessage.created_at).toLocaleString()}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    onClick={() => {
                      setReplyingTo(viewingMessage);
                      setReplyContent('');
                    }}
                    className="flex-1 bg-primary"
                    data-testid="reply-message-btn"
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      markContactMessageRead(viewingMessage.id, viewingMessage.is_read);
                      setViewingMessage(null);
                    }}
                    className="flex-1"
                  >
                    {viewingMessage.is_read ? 'Mark as Unread' : 'Mark as Read'}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => deleteContactMessage(viewingMessage.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reply to Contact Message Dialog */}
        <Dialog open={!!replyingTo} onOpenChange={(open) => !open && setReplyingTo(null)}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Reply to {replyingTo?.name}</DialogTitle>
            </DialogHeader>
            {replyingTo && (
              <div className="space-y-4 pt-4">
                <div className="bg-secondary/30 border border-border rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-2">Original message from {replyingTo.email}:</p>
                  <p className="font-medium mb-1">{replyingTo.subject}</p>
                  <p className="text-sm whitespace-pre-wrap">{replyingTo.message}</p>
                </div>

                <div className="space-y-2">
                  <Label>Your Reply</Label>
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply here..."
                    className="bg-input/50 border-transparent focus:border-primary min-h-[150px]"
                    data-testid="reply-textarea"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={sendReply}
                    disabled={sendingReply || !replyContent.trim()}
                    className="flex-1 bg-primary"
                    data-testid="send-reply-btn"
                  >
                    {sendingReply ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setReplyingTo(null)}
                    disabled={sendingReply}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminDashboard;
